import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import pubsub from '@/lib/pubsub'
import markupService from '@/services/markup-service'
import { v4 as uuid } from 'uuid'
import {
  nearestPointOnLine,
  lineSplit,
  pointToLineDistance,
  lineSlice,
} from '@turf/turf'
import { point } from '@turf/helpers'
import isEqual from 'lodash/isEqual'

/**
 * Line split mode
 */
class SplitMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(geoJson) {
    this.originalGeoJson = geoJson
    this.history = []
    this.undo = this._undo.bind(this)

    pubsub.subscribe('markupUndo', this.undo)
  }

  /**
   * @inheritDoc
   */
  onTearDown() {
    pubsub.unsubscribe('markupUndo', this.undo)
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const lines = this._formatLine(geoJson)
    const connectionPoints = this._formatSplitPoints(geoJson)
    return [...lines, ...connectionPoints]
  }

  /**
   * @inheritDoc
   */
  onClick(event) {
    const features = this.getFeaturesAtPoint(event.point)
    const focusedMarkup = markupService.getFocusedMarkup()
    const originalSegments = focusedMarkup.markup.properties?.segments ?? []
    const segments = [...originalSegments]

    const pointClickedOn = (feature) =>
      feature.properties.meta === 'connect-point'
    const lineClickedOn = (feature) =>
      feature.properties.lineId === this.originalGeoJson?.properties?.id ||
      feature.properties.id === this.originalGeoJson?.properties?.id
    const pointClicked = features.find(pointClickedOn)

    if (pointClicked) {
      this._removeLineSegment(segments, pointClicked)
    } else if (features.some(lineClickedOn)) {
      this._addLineSegment(segments, event)
    }

    if (!isEqual(segments, originalSegments)) {
      this.history.push(segments)
    }

    markupService.editFocusedMarkup({
      geo_json: { properties: { segments } },
    })
  }

  /**
   * Remove a segment from the array in place
   * @param {Array} segments - the array of segments
   * @param {Object} pointClicked - the point clicked on
   */
  _removeLineSegment(segments, pointClicked) {
    const segmentIndex = pointClicked.properties.segmentIndex
    // Don't do anything if they click the start or end point
    if (segmentIndex === 0 || segmentIndex === segments.length) {
      return
    }

    if (segments.length === 2) {
      // If there are only two segments to begin with clear the array
      segments.splice(0, 2)
    } else {
      // Replace the two affected segments with the new combined segment
      const startPoint = segments[segmentIndex - 1].geometry.coordinates[0]
      const endPoint =
        segments[segmentIndex].geometry.coordinates[
          segments[segmentIndex].geometry.coordinates.length - 1
        ]
      const line = lineSlice(startPoint, endPoint, this.originalGeoJson)
      segments.splice(segmentIndex - 1, 2, this._generateSegment(line))
    }
  }

  /**
   * Add a segment to the array in place
   * @param {Array} isPointInLine
   * @param {Object} event - mapbox click event
   */
  _addLineSegment(segments, event) {
    const coord = point([event.lngLat.lng, event.lngLat.lat])
    const nearestCoordOnLine = nearestPointOnLine(this.originalGeoJson, coord)

    if (segments.length === 0) {
      const lines = lineSplit(this.originalGeoJson, nearestCoordOnLine)
      // If for some reason the split doesn't work don't do anything
      if (lines.features.length < 2) {
        return
      }
      segments.push(
        this._generateSegment(lines.features[0]),
        this._generateSegment(lines.features[1])
      )
    } else {
      // Find the segment that contains the clicked point
      // FUTURE: Use turfs booleanPointOnLine when the nearestPointOnLine
      // accuracy bug has been resolved - https://github.com/Turfjs/turf/issues/2023
      const { indexToReplace } = segments.reduce(
        (accumulator, segment, index) => {
          const distance = pointToLineDistance(nearestCoordOnLine, segment)
          if (index === 0 || distance < accumulator.distance) {
            return {
              distance: distance,
              indexToReplace: index,
            }
          }
          return accumulator
        },
        {}
      )

      const lines = lineSplit(segments[indexToReplace], nearestCoordOnLine)
      // If for some reason the split doesn't work don't do anything
      if (lines.features.length < 2) {
        return
      }
      segments.splice(
        indexToReplace,
        1,
        this._generateSegment(lines.features[0]),
        this._generateSegment(lines.features[1])
      )
    }
  }

  /**
   * Generate geojson for line segment with expected properties
   * given the line geometry
   * @param {object} line - line geojson
   * @returns {object} - line segment geojson
   */
  _generateSegment(line) {
    return {
      ...line,
      properties: {
        ...this.originalGeoJson?.properties,
        lineId: this.originalGeoJson?.properties?.id,
        id: uuid(),
        opacity: 1,
      },
    }
  }

  /**
   * Generate an array of geojson objects that make up the line.
   * @param {object} geojson - the geojson of the line
   * @returns {array} - an array of geojson object making up the line
   */
  _formatLine(geoJson) {
    const segments = [...(geoJson?.properties?.segments ?? [])]
    if (!segments || segments.length === 0) {
      return [geoJson]
    }

    return segments
  }

  /**
   * Generate the array of geojson object making up each split point
   * and corresponding label for the line.
   * @param {object} geoJson - the geojson of the line
   * @returns {array} - an array of geojson object making up the split points and labels
   */
  _formatSplitPoints(geoJson) {
    const segments = geoJson?.properties?.segments

    // If we have not split the line yet return an empty array
    if (!segments || segments.length === 0) {
      return []
    }

    const features = []
    segments.forEach((segment, index) => {
      const coordinate = segment.geometry.coordinates[0]
      features.push(...this._formatSplitPointAndLabel(coordinate, index))
    })
    const finalCoord =
      geoJson.geometry.coordinates[geoJson.geometry.coordinates.length - 1]
    features.push(
      ...this._formatSplitPointAndLabel(finalCoord, segments.length)
    )

    return features
  }

  /**
   * Generate a single point and label geojson object for a segment
   * @param {array} coord - the coordinate to place the point and label on
   * @param {number} segmentIndex - the index of the segment, used to generate
   * the label
   * @returns {array} - an array of the point and label geojson objects for the segment
   */
  _formatSplitPointAndLabel(coord, segmentIndex) {
    return [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coord,
        },
        properties: {
          segmentIndex: segmentIndex,
          meta: 'connect-point',
        },
      },
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coord,
        },
        properties: {
          title: segmentIndex + 1,
          meta: 'point-label',
        },
      },
    ]
  }

  /**
   * Undo the last history event
   */
  _undo() {
    this.history.pop()
    const segments = this.history[this.history.length - 1]
    markupService.editFocusedMarkup({
      geo_json: { properties: { segments } },
    })
  }
}

export default SplitMode
